import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Input, message, Collapse, Row, Col, Descriptions, Select, Tag, Dropdown, Divider } from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import { DownOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { agentApi, type AgentDTO, type AgentApiPurpose, type AgentCredential } from '../api/kfc';
import { get56yorkyApiByName, getApiPath } from '../data/56yorkyApiCollection';
import ResponsiveDrawer from '../components/ResponsiveDrawer';

/** 单个能力 + API 详情卡片（含 56yorky 文档时展示 URL、参数、文档） */
const CapabilityApiCard: React.FC<{
  capability: string;
  apiName: string;
  agentIdentifier?: string;
}> = ({ capability, apiName, agentIdentifier }) => {
  const apiDoc = agentIdentifier === '56yorky' ? get56yorkyApiByName(apiName) : undefined;
  const path = apiDoc ? getApiPath(apiDoc) : '';
  const req = apiDoc?.request;

  return (
    <div style={{ marginBottom: 20, padding: 16, background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
      <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 15 }}>
        {capability}
        <span style={{ fontWeight: 400, color: '#666', marginLeft: 8 }}>→ {apiName}</span>
      </div>
      {apiDoc && req ? (
        <>
          <Descriptions column={1} size="small" style={{ marginBottom: 12 }}>
            <Descriptions.Item label="请求方式">{req.method}</Descriptions.Item>
            <Descriptions.Item label="API 路径">{path || req.url?.raw || '-'}</Descriptions.Item>
          </Descriptions>
          {req.header && req.header.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>请求头</div>
              <AdaptiveTable
                size="small"
                dataSource={req.header}
                rowKey="key"
                pagination={false}
                columns={[
                  { title: 'Key', dataIndex: 'key', width: 140 },
                  { title: 'Value/说明', dataIndex: 'value', ellipsis: true },
                ]}
                style={{ marginBottom: 12 }}
              />
            </>
          )}
          {req.url?.query && req.url.query.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Query 参数</div>
              <AdaptiveTable
                size="small"
                dataSource={req.url.query}
                rowKey="key"
                pagination={false}
                columns={[
                  { title: '参数', dataIndex: 'key', width: 120 },
                  { title: '说明', dataIndex: 'description', ellipsis: true },
                ]}
                style={{ marginBottom: 12 }}
              />
            </>
          )}
          {req.body?.raw && (
            <>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>请求体示例</div>
              <pre style={{ background: '#fff', padding: 8, borderRadius: 4, fontSize: 12, overflow: 'auto', maxHeight: 160 }}>{req.body.raw}</pre>
            </>
          )}
          {req.description && (
            <>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>文档说明</div>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontSize: 12, lineHeight: 1.5 }}>{req.description}</pre>
            </>
          )}
        </>
      ) : (
        <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>暂无该 API 的详细文档</div>
      )}
    </div>
  );
};

const DEFAULT_CREDENTIAL = 'default';

const ENDPOINTS: { url: string; credential: string; label: string }[] = [
  { url: 'logisticsSystemEndPoint', credential: 'logisticsCredential', label: '物流系统对接端点' },
  { url: 'priceEndPoint', credential: 'priceCredential', label: '查询价格端点' },
  { url: 'trackEndPoint', credential: 'trackCredential', label: '查询轨迹端点' },
  { url: 'labelEndPoint', credential: 'labelCredential', label: '面单打印端点' },
];

const TEST_PURPOSES: { key: AgentApiPurpose; label: string }[] = [
  { key: 'LOGISTICS', label: '物流系统' },
  { key: 'PRICE', label: '查询价格' },
  { key: 'TRACK', label: '查询轨迹' },
  { key: 'LABEL', label: '面单' },
];

type CredentialType = NonNullable<AgentCredential['type']>;

const credentialTypeOf = (c?: AgentCredential): CredentialType => c?.type ?? 'API_KEY';

const withCredentialType = (list?: AgentCredential[]): AgentCredential[] =>
  list?.length
    ? list.map((c) => ({ ...c, type: credentialTypeOf(c) }))
    : [{ name: DEFAULT_CREDENTIAL, type: 'API_KEY' }];

/** 不适用当前类型的字段置 null：Form.List 会连同已卸载字段一起提交，且 PUT 需显式清空 */
const sanitizeCredential = (c: AgentCredential): AgentCredential =>
  credentialTypeOf(c) === 'ACCOUNT'
    ? { ...c, appKey: null, appSecret: null, coid: null, nonce: null, version: null }
    : { ...c, username: null, password: null };

/** 端点绑定为空时显式回填 default，避免 PUT 逐字段合并时无法改回 default */
const withDefaultBindings = (apiConfig: AgentDTO['apiConfig'] = {}) => ({
  ...apiConfig,
  ...Object.fromEntries(ENDPOINTS.map((e) => [e.credential, (apiConfig as Record<string, string | undefined>)[e.credential] || DEFAULT_CREDENTIAL])),
  priceAccessMode: apiConfig.priceAccessMode || 'REST_API',
});

const AgentManage: React.FC = () => {
  const [list, setList] = useState<AgentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [capabilitiesDrawerOpen, setCapabilitiesDrawerOpen] = useState(false);
  const [capabilitiesAgent, setCapabilitiesAgent] = useState<AgentDTO | null>(null);
  const watchedCredentials = Form.useWatch('credentials', form) as AgentCredential[] | undefined;
  const credentialOptions = (watchedCredentials ?? [])
    .map((c) => c?.name?.trim())
    .filter((n): n is string => !!n)
    .map((n) => ({ value: n, label: n }));

  /** 切换类型时把另一组已有值带过来（仅目标为空时），如佳美达历史凭证把账号存在 appkey */
  const carryCredentialValues = (name: number, type: CredentialType) => {
    const c = (form.getFieldValue(['credentials', name]) ?? {}) as AgentCredential;
    const pairs: [keyof AgentCredential, keyof AgentCredential][] = type === 'ACCOUNT'
      ? [['username', 'appKey'], ['password', 'appSecret']]
      : [['appKey', 'username'], ['appSecret', 'password']];
    for (const [to, from] of pairs) {
      if (!c[to] && c[from]) form.setFieldValue(['credentials', name, to], c[from]);
    }
  };

  const load = () => {
    setLoading(true);
    agentApi.list()
      .then((res) => setList(res.data))
      .catch(() => { setList([]); message.warning('无法加载代理列表'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      basicInfo: { kfyAccountCode: 'KFY' },
      contact: { contactPerson: {} },
      officialWebsite: {},
      apiConfig: withDefaultBindings(),
      credentials: withCredentialType(),
      bankAccount: {},
      measurementSource: 'API',
    });
    setModalOpen(true);
  };

  const openEdit = (r: AgentDTO) => {
    setEditingId(r.id ?? null);
    form.setFieldsValue({
      basicInfo: r.basicInfo ?? {},
      contact: {
        contactPerson: r.contact?.contactPerson ?? {},
        address: r.contact?.address,
      },
      officialWebsite: r.officialWebsite ?? {},
      apiConfig: withDefaultBindings(r.apiConfig),
      credentials: withCredentialType(r.credentials),
      bankAccount: r.bankAccount ?? {},
      measurementSource: r.measurementSource ?? 'API',
    });
    setModalOpen(true);
  };

  const onFinish = (values: Record<string, any>) => {
    const dto: Partial<AgentDTO> = {
      basicInfo: { ...values.basicInfo, kfyAccountCode: (values.basicInfo?.kfyAccountCode ?? '').trim() },
      contact: values.contact,
      officialWebsite: values.officialWebsite,
      apiConfig: values.apiConfig,
      credentials: (values.credentials as AgentCredential[] | undefined)?.map(sanitizeCredential),
      bankAccount: values.bankAccount,
      measurementSource: values.measurementSource,
    };
    const submit = editingId
      ? agentApi.update(editingId, dto)
      : agentApi.create(dto);
    submit.then(() => {
      message.success(editingId ? '更新成功' : '创建成功');
      setModalOpen(false);
      load();
    }).catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const testConn = (id: string, purpose: AgentApiPurpose) => {
    const label = TEST_PURPOSES.find((p) => p.key === purpose)?.label ?? '';
    agentApi.testConnection(id, purpose).then((res) => {
      if (res.data.success) message.success(`${label}：连接成功`);
      else message.warning(`${label}：连接失败`);
    }).catch((e) => message.error(e.response?.data?.message || `${label}：测试失败`));
  };

  const openCapabilities = (r: AgentDTO) => {
    setCapabilitiesAgent(r);
    setCapabilitiesDrawerOpen(true);
  };

  return (
    <>
      <Card title="代理列表" extra={<Button type="primary" onClick={openAdd}>新增代理</Button>}>
        <AdaptiveTable
          loading={loading}
          dataSource={list}
          rowKey="id"
          scroll={{ x: 1100 }}
          columns={[
            { title: '代理名称', dataIndex: ['basicInfo', 'name'], width: 120, ellipsis: true },
            { title: '代理标识', dataIndex: ['basicInfo', 'identifier'], width: 120, ellipsis: true },
            {
              title: '开户代码',
              dataIndex: ['basicInfo', 'kfyAccountCode'],
              width: 110,
              ellipsis: true,
              render: (v?: string) => (v ? v : <span style={{ color: '#999' }}>无</span>),
            },
            { title: '对接人', dataIndex: ['contact', 'contactPerson', 'name'], width: 100, ellipsis: true },
            { title: '对接人电话', dataIndex: ['contact', 'contactPerson', 'phone'], width: 120, ellipsis: true },
            { title: '地址', dataIndex: ['contact', 'address'], width: 150, ellipsis: true },
            { title: '官网地址', dataIndex: ['officialWebsite', 'url'], width: 150, ellipsis: true },
            {
              title: '测量数据来源',
              dataIndex: 'measurementSource',
              width: 120,
              render: (v?: string) =>
                v === 'MANUAL' ? <Tag color="orange">手动上传</Tag> : <Tag>API 同步</Tag>,
            },
            {
              title: '操作',
              width: 240,
              fixed: 'right',
              render: (_, r) => (
                <>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
                  <Button type="link" size="small" onClick={() => openCapabilities(r)}>查看代理能力</Button>
                  <Dropdown
                    menu={{
                      items: TEST_PURPOSES.map((p) => ({ key: p.key, label: p.label })),
                      onClick: ({ key }) => r.id && testConn(r.id, key as AgentApiPurpose),
                    }}
                  >
                    <Button type="link" size="small">测试连接 <DownOutlined /></Button>
                  </Dropdown>
                </>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑代理' : '新增代理'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={720}
        bodyStyle={{ maxHeight: '70vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
          <div style={{ overflowY: 'auto', flex: 1, marginRight: -8, paddingRight: 8 }}>
            <Collapse
              defaultActiveKey={['basicInfo', 'contact', 'officialWebsite', 'apiConfig', 'bankAccount']}
              items={[
              {
                key: 'basicInfo',
                label: '基本信息',
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={['basicInfo', 'name']} label="代理名称" rules={[{ required: true, message: '请输入代理名称' }]}>
                        <Input placeholder="代理名称*" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['basicInfo', 'identifier']} label="代理标识" rules={[{ required: true, message: '请输入代理标识' }]}>
                        <Input placeholder="代理标识*" disabled={!!editingId} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={['basicInfo', 'kfyAccountCode']}
                        label="凯飞越开户代码"
                        extra="凯飞越在该代理系统中的开户代码，默认 KFY"
                      >
                        <Input placeholder="如 KFY；该代理无开户代码时清空" allowClear maxLength={64} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="measurementSource"
                        label="测量数据来源"
                        extra="手动上传：运单详情「入仓数据」可上传测量文字/图片，由大模型解析后确认"
                      >
                        <Select
                          options={[
                            { value: 'API', label: 'API 同步' },
                            { value: 'MANUAL', label: '手动上传（大模型解析）' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'contact',
                label: '联系信息',
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={['contact', 'contactPerson', 'name']} label="对接人名称" rules={[{ required: true, message: '请输入对接人名称' }]}>
                        <Input placeholder="对接人名称*" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['contact', 'contactPerson', 'phone']} label="对接人电话" rules={[{ required: true, message: '请输入对接人电话' }]}>
                        <Input placeholder="对接人电话*" />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item name={['contact', 'address']} label="地址">
                        <Input placeholder="地址" />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'officialWebsite',
                label: '官网',
                children: (
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item name={['officialWebsite', 'url']} label="官网地址" rules={[{ required: true, message: '请输入官网地址' }]}>
                        <Input placeholder="官网地址*" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['officialWebsite', 'username']} label="用户名">
                        <Input placeholder="用户名" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['officialWebsite', 'password']} label="密码">
                        <Input.Password placeholder="密码" autoComplete="new-password" />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'apiConfig',
                label: 'API 配置',
                children: (
                  <>
                    <Divider orientation="left" orientationMargin={0} style={{ marginTop: 0 }}>凭证</Divider>
                    <Form.List name="credentials">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name }) => {
                            const isDefault = form.getFieldValue(['credentials', name, 'name']) === DEFAULT_CREDENTIAL;
                            const isAccount = credentialTypeOf(watchedCredentials?.[name]) === 'ACCOUNT';
                            return (
                              <div key={key} style={{ marginBottom: 12, padding: '12px 12px 0', border: '1px solid #f0f0f0', borderRadius: 8 }}>
                                <Row gutter={16}>
                                  <Col span={12}>
                                    <Form.Item
                                      name={[name, 'name']}
                                      label="凭证名称"
                                      rules={[
                                        { required: true, whitespace: true, message: '请输入凭证名称' },
                                        {
                                          validator: (_, v?: string) => {
                                            const n = v?.trim().toLowerCase();
                                            const dup = (form.getFieldValue('credentials') as AgentCredential[] ?? [])
                                              .filter((c) => c?.name?.trim().toLowerCase() === n).length > 1;
                                            return dup ? Promise.reject(new Error('凭证名称重复')) : Promise.resolve();
                                          },
                                        },
                                      ]}
                                    >
                                      <Input placeholder="如 web" disabled={isDefault} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={isDefault ? 12 : 10}>
                                    <Form.Item name={[name, 'type']} label="类型" rules={[{ required: true, message: '请选择类型' }]}>
                                      <Select
                                        onChange={(t: CredentialType) => carryCredentialValues(name, t)}
                                        options={[
                                          { value: 'API_KEY', label: 'API 授权（appkey / appsecret）' },
                                          { value: 'ACCOUNT', label: '账号密码' },
                                        ]}
                                      />
                                    </Form.Item>
                                  </Col>
                                  {!isDefault && (
                                    <Col span={2} style={{ display: 'flex', alignItems: 'center' }}>
                                      <Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                                    </Col>
                                  )}
                                  {isAccount ? (
                                    <>
                                      <Col span={12}>
                                        <Form.Item name={[name, 'username']} label="用户名">
                                          <Input placeholder="用户名" autoComplete="off" />
                                        </Form.Item>
                                      </Col>
                                      <Col span={12}>
                                        <Form.Item name={[name, 'password']} label="密码">
                                          <Input.Password placeholder="密码" autoComplete="new-password" />
                                        </Form.Item>
                                      </Col>
                                    </>
                                  ) : (
                                    <>
                                      <Col span={12}>
                                        <Form.Item name={[name, 'appKey']} label="appkey">
                                          <Input placeholder="appkey" />
                                        </Form.Item>
                                      </Col>
                                      <Col span={12}>
                                        <Form.Item name={[name, 'appSecret']} label="appsecret / token">
                                          <Input.Password placeholder="appsecret" autoComplete="new-password" />
                                        </Form.Item>
                                      </Col>
                                      <Col span={8}>
                                        <Form.Item name={[name, 'coid']} label="coid（哲盟）">
                                          <Input placeholder="如 DEVCN" />
                                        </Form.Item>
                                      </Col>
                                      <Col span={8}>
                                        <Form.Item name={[name, 'nonce']} label="nonce（哲盟）">
                                          <Input placeholder="默认 slnkda" />
                                        </Form.Item>
                                      </Col>
                                      <Col span={8}>
                                        <Form.Item name={[name, 'version']} label="version（哲盟）">
                                          <Input placeholder="默认 1.0" />
                                        </Form.Item>
                                      </Col>
                                    </>
                                  )}
                                  <Col span={24}>
                                    <Form.Item name={[name, 'remark']} label="备注">
                                      <Input placeholder="备注" />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </div>
                            );
                          })}
                          <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ type: 'ACCOUNT' })} style={{ marginBottom: 16 }}>
                            新增凭证
                          </Button>
                        </>
                      )}
                    </Form.List>
                    <Divider orientation="left" orientationMargin={0}>端点</Divider>
                    <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, marginBottom: 12 }}>
                      端点为空时，该类调用沿用物流系统对接端点及其绑定的凭证
                    </div>
                    {ENDPOINTS.map((ep) => (
                      <Row gutter={16} key={ep.url}>
                        <Col span={ep.url === 'priceEndPoint' ? 10 : 16}>
                          <Form.Item name={['apiConfig', ep.url]} label={ep.label}>
                            <Input placeholder={ep.label} />
                          </Form.Item>
                        </Col>
                        <Col span={ep.url === 'priceEndPoint' ? 7 : 8}>
                          <Form.Item name={['apiConfig', ep.credential]} label="凭证">
                            <Select options={credentialOptions} />
                          </Form.Item>
                        </Col>
                        {ep.url === 'priceEndPoint' && (
                          <Col span={7}>
                            <Form.Item name={['apiConfig', 'priceAccessMode']} label="访问方式">
                              <Select
                                options={[
                                  { value: 'REST_API', label: 'REST API' },
                                  { value: 'WEB_PAGE', label: '网页（账号登录）' },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                    ))}
                  </>
                ),
              },
              {
                key: 'bankAccount',
                label: '银行账户',
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={['bankAccount', 'bankName']} label="开户行">
                        <Input placeholder="开户行" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['bankAccount', 'accountName']} label="开户名">
                        <Input placeholder="开户名" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['bankAccount', 'accountNumber']} label="银行账号">
                        <Input placeholder="银行账号" />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
            ]}
            />
          </div>
          <Form.Item style={{ marginTop: 16, marginBottom: 0, flexShrink: 0 }}>
            <Button type="primary" htmlType="submit">保存</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>取消</Button>
          </Form.Item>
        </Form>
      </Modal>
      <ResponsiveDrawer
        title={`代理能力 - ${capabilitiesAgent?.basicInfo?.name ?? capabilitiesAgent?.basicInfo?.identifier ?? ''}`}
        size="md"
        open={capabilitiesDrawerOpen}
        onClose={() => { setCapabilitiesDrawerOpen(false); setCapabilitiesAgent(null); }}
        closable
        footer={null}
      >
        {capabilitiesAgent && (
          <>
            {capabilitiesAgent.capabilities?.length ? (
              <div style={{ paddingRight: 8 }}>
                {capabilitiesAgent.capabilities.map((cap, i) => (
                  <CapabilityApiCard
                    key={i}
                    capability={cap.capability ?? ''}
                    apiName={cap.apiName ?? ''}
                    agentIdentifier={capabilitiesAgent.basicInfo?.identifier}
                  />
                ))}
              </div>
            ) : (
              <div style={{ color: 'rgba(0,0,0,0.45)' }}>暂无配置的代理能力映射</div>
            )}
          </>
        )}
      </ResponsiveDrawer>
    </>
  );
};

export default AgentManage;
